<script setup lang="ts">
import { onBeforeUnmount, watch } from 'vue'

export type ToastSeverity = 'neutral' | 'warn'
export type ToastPosition = 'bottom-right' | 'top-right'

const props = withDefaults(
    defineProps<{
        open: boolean
        severity?: ToastSeverity
        position?: ToastPosition
        closable?: boolean
        closeLabel?: string
        /** Auto-close delay in ms. 0 = stays until closed. */
        life?: number
    }>(),
    {
        severity: 'neutral',
        position: 'bottom-right',
        closable: true,
        closeLabel: 'Cerrar',
        life: 0
    }
)

const emit = defineEmits<{ close: [] }>()

let timer: ReturnType<typeof setTimeout> | undefined

function clearTimer() {
    clearTimeout(timer)
    timer = undefined
}

watch(
    () => [props.open, props.life] as const,
    ([open, life]) => {
        clearTimer()
        if (open && life > 0) {
            timer = setTimeout(() => emit('close'), life)
        }
    },
    { immediate: true }
)

onBeforeUnmount(clearTimer)
</script>

<template>
    <Teleport to="body">
        <Transition name="toast-fade">
            <div
                v-if="open"
                class="ui-toast"
                :class="[`ui-toast--${severity}`, `ui-toast--${position}`]"
                :role="severity === 'warn' ? 'alert' : 'status'"
            >
                <div class="toast-body">
                    <slot />
                </div>
                <button
                    v-if="closable"
                    class="toast-close"
                    type="button"
                    :aria-label="closeLabel"
                    @click="$emit('close')"
                >
                    ✕
                </button>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
.ui-toast {
    --toast-bg: var(--hh-toast-neutral);
    --toast-text: var(--hh-text-inverse);
    --toast-shift: 0.5rem; /* slide direction on enter/leave */

    position: fixed;
    right: max(1rem, env(safe-area-inset-right));
    z-index: 1000;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    max-width: 28rem;
    padding: 0.75rem 1rem;
    background: var(--toast-bg);
    color: var(--toast-text);
    border-radius: 0.5rem;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
    font-size: 0.85rem;
    line-height: 1.3;
}

/* severity */
.ui-toast--warn {
    --toast-bg: var(--hh-toast-warn);
}

/* position */
.ui-toast--bottom-right {
    bottom: max(1rem, env(safe-area-inset-bottom));
}
.ui-toast--top-right {
    top: max(1rem, env(safe-area-inset-top));
    --toast-shift: -0.5rem;
}

.toast-body {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.toast-body :deep(a) {
    color: inherit;
    text-decoration: underline;
}

.toast-close {
    flex-shrink: 0;
    background: none;
    border: none;
    color: var(--toast-text);
    /* opacity: 0.7; */
    cursor: pointer;
    font-size: 1rem;
    line-height: 1;
    padding: 0 0 0 0.8rem;
}
.toast-close:hover {
    opacity: 1;
}
.toast-close:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
    border-radius: 0.25rem;
}

.toast-fade-enter-active,
.toast-fade-leave-active {
    transition:
        opacity 0.25s ease,
        transform 0.25s ease;
}
.toast-fade-enter-from,
.toast-fade-leave-to {
    opacity: 0;
    transform: translateY(var(--toast-shift));
}

@media (prefers-reduced-motion: reduce) {
    .toast-fade-enter-active,
    .toast-fade-leave-active {
        transition: opacity 0.15s ease;
    }
    .toast-fade-enter-from,
    .toast-fade-leave-to {
        transform: none;
    }
}
</style>
